import { useState, useMemo, useRef, useEffect } from 'preact/hooks'
import { formatTime } from '../utils/dateUtils'
import { isSessionHappeningNow, isSessionEnded } from '../utils/timeUtils'
import { SessionCalendarButton } from './SessionCalendarButton'
import analytics from '../utils/analytics'

export function VisualTimelineView({ 
  sessions, 
  starredSessions, 
  categoryFilter,
  onSessionClick,
  onToggleStar,
  onExportComplete,
  currentTime = new Date()
}) {
  const [hoveredSession, setHoveredSession] = useState(null)
  const [zoomLevel, setZoomLevel] = useState(1) // 1x, 2x, 4x zoom levels
  const [touchZoom, setTouchZoom] = useState(1) // Additional touch zoom multiplier
  const timelineRef = useRef(null)
  
  // Handle pinch zoom on mobile
  useEffect(() => {
    let initialDistance = 0
    let initialZoom = 1
    let isZooming = false
    
    const handleTouchStart = (e) => {
      if (e.touches.length === 2) {
        isZooming = true
        initialZoom = touchZoom // Store the zoom level when gesture starts
        e.preventDefault() // Prevent browser zoom
        
        const touch1 = e.touches[0]
        const touch2 = e.touches[1]
        initialDistance = Math.hypot(
          touch1.clientX - touch2.clientX,
          touch1.clientY - touch2.clientY
        )
      }
    }
    
    const handleTouchMove = (e) => {
      if (e.touches.length === 2 && isZooming && initialDistance > 0) {
        e.preventDefault() // Prevent browser zoom
        
        const touch1 = e.touches[0]
        const touch2 = e.touches[1]
        const currentDistance = Math.hypot(
          touch1.clientX - touch2.clientX,
          touch1.clientY - touch2.clientY
        )
        
        // Calculate scale based on distance change from initial
        const scale = currentDistance / initialDistance
        
        // Apply scale to initial zoom level
        const newZoom = Math.max(0.5, Math.min(4, initialZoom * scale))
        
        // Update zoom if there's a meaningful change
        if (Math.abs(newZoom - touchZoom) > 0.02) {
          setTouchZoom(newZoom)
        }
      }
    }
    
    const handleTouchEnd = (e) => {
      if (isZooming) {
        e.preventDefault() // Prevent browser zoom
        
        // Track the final zoom level for analytics
        if (Math.abs(touchZoom - 1) > 0.1) {
          analytics.timelineZoom(touchZoom, 'pinch')
        }
      }
      
      isZooming = false
      initialDistance = 0
      initialZoom = 1
    }
    
    // Prevent browser zoom on the timeline container
    const handleGestureStart = (e) => e.preventDefault()
    const handleGestureChange = (e) => e.preventDefault()
    const handleGestureEnd = (e) => e.preventDefault()
    
    const element = timelineRef.current
    if (element) {
      // Touch events with passive: false to prevent default behavior
      element.addEventListener('touchstart', handleTouchStart, { passive: false })
      element.addEventListener('touchmove', handleTouchMove, { passive: false })
      element.addEventListener('touchend', handleTouchEnd, { passive: false })
      element.addEventListener('touchcancel', handleTouchEnd, { passive: false })
      
      // Gesture events (Safari)
      element.addEventListener('gesturestart', handleGestureStart, { passive: false })
      element.addEventListener('gesturechange', handleGestureChange, { passive: false })
      element.addEventListener('gestureend', handleGestureEnd, { passive: false })
      
      return () => {
        element.removeEventListener('touchstart', handleTouchStart)
        element.removeEventListener('touchmove', handleTouchMove)
        element.removeEventListener('touchend', handleTouchEnd)
        element.removeEventListener('touchcancel', handleTouchEnd)
        element.removeEventListener('gesturestart', handleGestureStart)
        element.removeEventListener('gesturechange', handleGestureChange)
        element.removeEventListener('gestureend', handleGestureEnd)
      }
    }
  }, [touchZoom]) // Add touchZoom to dependencies
  
  // Process sessions for visual timeline
  const timelineData = useMemo(() => {
    const filteredSessions = categoryFilter === 'All' 
      ? sessions.filter(session => 
          // Filter out Welcome Party and other non-conference sessions from visual timeline
          !session.title.toLowerCase().includes('welcome party') &&
          !session.contentType?.toLowerCase().includes('party') &&
          session.category !== 'Fun Stuff'
        )
      : sessions.filter(session => 
          session.category === categoryFilter &&
          !session.title.toLowerCase().includes('welcome party') &&
          !session.contentType?.toLowerCase().includes('party') &&
          session.category !== 'Fun Stuff'
        )
    
    if (filteredSessions.length === 0) return { sessions: [], timeRange: null, lanes: [] }
    
    // Sort sessions by start time
    const sortedSessions = [...filteredSessions].sort((a, b) => 
      new Date(a.startTime) - new Date(b.startTime)
    )
    
    // Calculate time range
    const startTimes = sortedSessions.map(s => new Date(s.startTime))
    const endTimes = sortedSessions.map(s => new Date(s.endTime))
    const earliestStart = new Date(Math.min(...startTimes))
    const latestEnd = new Date(Math.max(...endTimes))
    
    // Create timeline lanes to avoid overlaps
    const lanes = []
    
    sortedSessions.forEach(session => {
      const sessionStart = new Date(session.startTime)
      const sessionEnd = new Date(session.endTime)
      
      // Find the first available lane
      let laneIndex = 0
      while (laneIndex < lanes.length) {
        const lane = lanes[laneIndex]
        const hasConflict = lane.some(existingSession => {
          const existingStart = new Date(existingSession.startTime)
          const existingEnd = new Date(existingSession.endTime)
          return sessionStart < existingEnd && sessionEnd > existingStart
        })
        
        if (!hasConflict) {
          lane.push(session)
          break
        }
        laneIndex++
      }
      
      // Create new lane if needed
      if (laneIndex >= lanes.length) {
        lanes.push([session])
      }
    })
    
    return {
      sessions: sortedSessions,
      timeRange: { start: earliestStart, end: latestEnd },
      lanes
    }
  }, [sessions, categoryFilter])
  
  // Calculate position and width for session bars
  const getSessionStyle = (session) => {
    if (!timelineData.timeRange) return { left: '0%', width: '0%' }
    
    const sessionStart = new Date(session.startTime)
    const sessionEnd = new Date(session.endTime)
    const totalDuration = timelineData.timeRange.end - timelineData.timeRange.start
    
    const startOffset = sessionStart - timelineData.timeRange.start
    const sessionDuration = sessionEnd - sessionStart
    
    // Calculate positions as percentages of the base timeline (not zoomed)
    const left = (startOffset / totalDuration) * 100
    const width = (sessionDuration / totalDuration) * 100
    
    return {
      left: `${left}%`,
      width: `${Math.max(width, 2)}%` // Minimum 2% width for visibility
    }
  }
  
  // Generate time markers based on zoom level
  const timeMarkers = useMemo(() => {
    if (!timelineData.timeRange) return []
    
    const markers = []
    const start = new Date(timelineData.timeRange.start)
    const end = new Date(timelineData.timeRange.end)
    
    // Adjust marker interval based on zoom level
    const minuteInterval = zoomLevel >= 4 ? 15 : zoomLevel >= 2 ? 30 : 60
    
    const current = new Date(start)
    if (minuteInterval === 60) {
      current.setMinutes(0, 0, 0) // Round to nearest hour
    } else {
      const minutes = current.getMinutes()
      const roundedMinutes = Math.floor(minutes / minuteInterval) * minuteInterval
      current.setMinutes(roundedMinutes, 0, 0)
    }
    
    while (current <= end) {
      const totalDuration = end - start
      const offset = current - start
      const position = (offset / totalDuration) * 100
      
      markers.push({
        time: new Date(current),
        position: Math.max(0, Math.min(100, position))
      })
      
      current.setMinutes(current.getMinutes() + minuteInterval)
    }
    
    return markers
  }, [timelineData.timeRange, zoomLevel])
  
  // Current time position
  const currentTimePosition = useMemo(() => {
    if (!timelineData.timeRange) return null
    
    const totalDuration = timelineData.timeRange.end - timelineData.timeRange.start
    const currentOffset = currentTime - timelineData.timeRange.start
    const position = (currentOffset / totalDuration) * 100
    
    return Math.max(0, Math.min(100, position))
  }, [currentTime, timelineData.timeRange])

  // Zoom controls
  const zoomLevels = [
    { level: 1, label: '1x', description: 'Overview' },
    { level: 2, label: '2x', description: 'Detailed' },
    { level: 4, label: '4x', description: 'Fine-grained' }
  ]
  
  // Combined zoom level (button zoom * touch zoom)
  const effectiveZoomLevel = zoomLevel * touchZoom
  
  const SessionBar = ({ session, laneIndex }) => {
    const isStarred = starredSessions.includes(session.id)
    const isLive = isSessionHappeningNow(session, currentTime)
    const isEnded = isSessionEnded(session, currentTime)
    const isHovered = hoveredSession === session.id
    const style = getSessionStyle(session)
    
    const categoryColors = {
      'Dev mode': 'from-editions-green/80 to-editions-green/60',
      'Biz mode': 'from-editions-purple/80 to-editions-purple/60',
      'General': 'from-gray-600/80 to-gray-500/60'
    }
    
    // Calculate if session is too narrow to show content
    const sessionWidth = parseFloat(style.width)
    const isNarrow = sessionWidth < 8 // Less than 8% width
    
    // Calculate minimum width based on content being displayed
    const getMinWidth = () => {
      if (isNarrow) return '80px'
      if (zoomLevel >= 2) return '160px' // Larger width for time display
      return '120px' // Default width
    }
    
    return (
      <div
        className={`
          absolute rounded-lg border cursor-pointer transition-all duration-200
          bg-gradient-to-r ${categoryColors[session.category] || categoryColors['General']}
          ${isStarred ? 'ring-2 ring-editions-orange/60' : 'border-white/20'}
          ${isLive ? 'ring-2 ring-editions-green/80 shadow-lg shadow-editions-green/20' : ''}
          ${isEnded ? 'opacity-50 grayscale' : ''}
          ${isHovered ? 'z-20 scale-105 shadow-xl' : 'z-10'}
          hover:z-20 hover:scale-105 hover:shadow-xl
        `}
        style={{ 
          ...style, 
          top: `${laneIndex * 60}px`, // Fixed: removed extra padding
          height: '48px', // Fixed height
          minWidth: getMinWidth()
        }}
        onClick={() => onSessionClick?.(session.id)}
        onMouseEnter={() => setHoveredSession(session.id)}
        onMouseLeave={() => setHoveredSession(null)}
      >
        <div className="flex items-center h-full px-2 gap-1">
          {/* Status indicator - always visible */}
          {isLive && (
            <div className="w-2 h-2 bg-editions-green rounded-full animate-pulse flex-shrink-0"></div>
          )}
          {isEnded && !isLive && (
            <div className="w-2 h-2 bg-gray-500 rounded-full flex-shrink-0"></div>
          )}
          
          {/* Session content - adaptive to width */}
          {isNarrow ? (
            // Narrow view - minimal content
            <div className="flex-1 min-w-0 text-center">
              <div className="text-white text-xs font-bold truncate">
                {session.title.split(' ')[0]}
              </div>
            </div>
          ) : (
            // Normal view - full content
            <>
              {/* Session title */}
              <div className="flex-1 min-w-0">
                <div className="text-white text-sm font-medium truncate">
                  {session.title}
                </div>
                {zoomLevel >= 2 && (
                  <div className="text-white/70 text-xs leading-tight">
                    <span className="inline-block">
                      {formatTime(session.startTime)} - {formatTime(session.endTime)}
                    </span>
                  </div>
                )}
              </div>
              
              {/* Speaker avatars - only show in fine-grained zoom for more space */}
              {zoomLevel >= 4 && session.speakers && session.speakers.length > 0 && (
                <div className="flex -space-x-1 flex-shrink-0">
                  {session.speakers.slice(0, 2).map((speaker, idx) => (
                    <div 
                      key={idx}
                      className="w-5 h-5 rounded-full bg-white/20 border border-white/30 flex items-center justify-center"
                      title={speaker.name}
                    >
                      <span className="text-xs text-white font-medium">
                        {speaker.name.charAt(0)}
                      </span>
                    </div>
                  ))}
                  {session.speakers.length > 2 && (
                    <div className="w-5 h-5 rounded-full bg-white/20 border border-white/30 flex items-center justify-center">
                      <span className="text-xs text-white">+{session.speakers.length - 2}</span>
                    </div>
                  )}
                </div>
              )}
              
              {/* Action buttons - only in fine-grained zoom */}
              {zoomLevel >= 4 && (
                <div className="flex items-center gap-1 flex-shrink-0">
                  <SessionCalendarButton
                    session={session}
                    isExpanded={false}
                    onExportComplete={onExportComplete}
                    size="sm"
                  />
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      onToggleStar?.(session.id)
                    }}
                    className={`
                      p-1 rounded transition-colors
                      ${isStarred 
                        ? 'text-editions-orange hover:bg-editions-orange/20' 
                        : 'text-white/70 hover:text-editions-orange hover:bg-white/10'
                      }
                    `}
                  >
                    <svg className="w-3 h-3" fill={isStarred ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                    </svg>
                  </button>
                </div>
              )}
            </>
          )}
        </div>
        
        {/* Enhanced hover tooltip */}
        {isHovered && (
          <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 p-3 bg-black/95 rounded-lg border border-white/20 text-xs text-white whitespace-nowrap z-30 max-w-xs">
            <div className="font-medium mb-1">{session.title}</div>
            <div className="text-white/70 mb-1">📍 {session.location}</div>
            <div className="text-white/70 mb-1">🕒 {formatTime(session.startTime)} - {formatTime(session.endTime)}</div>
            <div className="text-white/70 mb-1">🏷️ {session.category} • {session.contentType}</div>
            {session.speakers && session.speakers.length > 0 && (
              <div className="text-white/70">👤 {session.speakers.map(s => s.name).join(', ')}</div>
            )}
            {isStarred && (
              <div className="text-editions-orange mt-1">⭐ Starred</div>
            )}
          </div>
        )}
      </div>
    )
  }
  
  if (timelineData.sessions.length === 0) {
    return (
      <div className="text-center py-12 text-gray-400">
        <div className="text-4xl mb-4">📅</div>
        <h3 className="text-lg font-medium text-white mb-2">No sessions found</h3>
        <p className="text-sm">
          {categoryFilter !== 'All' 
            ? `No sessions in ${categoryFilter} category` 
            : 'No sessions available'
          }
        </p>
      </div>
    )
  }
  
  return (
    <div className="space-y-4">
      {/* Header with Zoom Controls */}
      <div className="flex flex-col gap-4">
        <div className="text-center">
          <h2 className="text-xl sm:text-2xl font-bold text-white mb-1">Visual Timeline</h2>
          <p className="text-gray-400 text-sm">
            See all sessions in a visual timeline • Hover for details
          </p>
          {categoryFilter !== 'All' && (
            <div className="mt-2">
              <span className="text-xs bg-editions-purple/20 text-editions-purple-light px-3 py-1 rounded-full">
                Filtered: {categoryFilter}
              </span>
            </div>
          )}
        </div>
        
        {/* Zoom Controls - Responsive Layout */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center justify-center sm:justify-start gap-2">
            <span className="text-sm text-gray-400">Zoom:</span>
            {zoomLevels.map((zoom) => (
              <button
                key={zoom.level}
                onClick={() => {
                  setZoomLevel(zoom.level)
                  analytics.timelineZoom(zoom.level, 'button')
                }}
                className={`
                  px-3 py-1.5 rounded-lg text-sm font-medium border transition-all duration-200
                  ${zoomLevel === zoom.level
                    ? 'bg-editions-purple text-white border-editions-purple'
                    : 'bg-transparent text-gray-300 border-gray-600 hover:text-white hover:border-editions-purple/50'
                  }
                `}
                title={zoom.description}
              >
                {zoom.label}
              </button>
            ))}
          </div>
          
          {/* Pinch Zoom Indication */}
          <div className="flex items-center justify-center sm:justify-end gap-2">
            {touchZoom !== 1 ? (
              // Show current touch zoom level
              <>
                <span className="text-xs text-gray-400">Touch zoom:</span>
                <span className="text-xs bg-editions-green/20 text-editions-green px-2 py-1 rounded">
                  {touchZoom.toFixed(1)}x
                </span>
                <button
                  onClick={() => {
                    setTouchZoom(1)
                    analytics.timelineZoom(1, 'touch_reset')
                  }}
                  className="text-xs text-gray-400 hover:text-white underline"
                >
                  Reset
                </button>
              </>
            ) : (
              // Show pinch zoom instruction
              <div className="flex items-center gap-1 text-xs text-gray-400">
                <span className="animate-pulse">👇</span>
                <span className="hidden sm:inline">Pinch to zoom on mobile</span>
                <span className="sm:hidden">Pinch to zoom</span>
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Timeline Container */}
      <div 
        ref={timelineRef}
        className="bg-white/5 rounded-xl border border-white/10 p-3 sm:p-4 overflow-auto select-none"
        style={{ 
          touchAction: 'pan-x manipulation', // Only allow horizontal panning, disable zoom
          userSelect: 'none',
          WebkitUserSelect: 'none',
          msUserSelect: 'none'
        }}
      >
        {/* Time axis - Improved mobile visibility */}
        <div className="relative mb-4 sm:mb-6">
          {/* Dynamic time range based on zoom level */}
          <div className="flex justify-between items-center mb-2">
            <span style={{ fontSize: '0.5rem' }} className="text-gray-400 font-medium">
              {timeMarkers.length > 0 ? formatTime(timeMarkers[0].time) : formatTime(timelineData.timeRange.start)}
            </span>
            <div style={{ fontSize: '0.5rem' }} className="text-gray-400 font-medium text-center">
              {effectiveZoomLevel > 1 && (
                <span className="bg-editions-purple/20 text-editions-purple-light px-2 py-1 rounded text-xs">
                  {effectiveZoomLevel.toFixed(1)}x zoom
                </span>
              )}
            </div>
            <span style={{ fontSize: '0.5rem' }} className="text-gray-400 font-medium">
              {timeMarkers.length > 0 ? formatTime(timeMarkers[timeMarkers.length - 1].time) : formatTime(timelineData.timeRange.end)}
            </span>
          </div>
          
          {/* Time markers */}
          <div 
            className="relative h-8 bg-white/5 rounded"
            style={{ 
              minWidth: `${Math.max(100 * effectiveZoomLevel, 100)}%`,
              width: `${100 * effectiveZoomLevel}%`
            }}
          >
            {timeMarkers.map((marker, idx) => (
              <div 
                key={idx}
                className="absolute top-0 bottom-0 border-l border-white/20"
                style={{ left: `${marker.position}%` }}
              >
                <div className="absolute -top-6 left-0 transform -translate-x-1/2 text-sm text-gray-400 whitespace-nowrap font-medium">
                  {formatTime(marker.time)}
                </div>
              </div>
            ))}
            
            {/* Current time indicator */}
            {currentTimePosition !== null && (
              <div 
                className="absolute top-0 bottom-0 border-l-2 border-editions-green z-10"
                style={{ left: `${currentTimePosition}%` }}
              >
                <div className="absolute -top-8 left-0 transform -translate-x-1/2 bg-editions-green text-white text-xs px-2 py-1 rounded whitespace-nowrap">
                  NOW
                </div>
              </div>
            )}
          </div>
        </div>
        
        {/* Sessions Timeline */}
        <div 
          className="relative"
          style={{ 
            height: `${timelineData.lanes.length * 60}px`, 
            minHeight: '120px',
            minWidth: `${Math.max(100 * effectiveZoomLevel, 100)}%`,
            width: `${100 * effectiveZoomLevel}%`
          }}
        >
          {timelineData.lanes.map((lane, laneIndex) =>
            lane.map(session => (
              <SessionBar 
                key={session.id} 
                session={session} 
                laneIndex={laneIndex}
              />
            ))
          )}
        </div>
      </div>
      
      {/* Legend */}
      <div className="bg-white/5 rounded-xl border border-white/10 p-4">
        <h4 className="font-medium text-white mb-3">Legend & Controls</h4>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs mb-3">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-editions-green rounded-full animate-pulse"></div>
            <span className="text-gray-300">Live Now</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-editions-orange rounded border-2 border-editions-orange"></div>
            <span className="text-gray-300">Starred Session</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-gray-500 rounded"></div>
            <span className="text-gray-300">Ended Session</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-6 h-2 bg-gradient-to-r from-editions-green/80 to-editions-green/60 rounded"></div>
            <span className="text-gray-300">Dev Mode</span>
          </div>
        </div>
        <div className="text-xs text-gray-400">
          💡 Use zoom controls to adjust detail level • Scroll horizontally to navigate timeline • Hover sessions for details
        </div>
      </div>
    </div>
  )
} 